<template>
    <v-app class="ppv2-bg-white dark:ppv2-bg-dark ppv2-rounded-3xl ppv2-p-6">
        <!-- Header -->
        <div class="d-flex flex-row align-items-center ppv2-mb-6">
            <h1 class="ppv2-text-black poppinsRegular uppercaseFirstLetter mb-0" :style="{ fontSize: normalizeFontSize(26) }">
                {{ isEdit ? $t('tag.titleUpdateTag') : $t('tag.titleTag') }}
            </h1>
        </div>

        <form v-if="!isDataLoading" @submit.prevent="submit">
            <div class="ppv2-flex ppv2-flex-col md:ppv2-flex-row ppv2-gap-6">
                <!-- LEFT COLUMN -->
                <div class="ppv2-w-full md:ppv2-w-[63%]">
                    <!-- Main Info Card -->
                    <div
                        class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-p-6 ppv2-mb-6 ppv2-shadow-none"
                    >
                        <div
                            class="ppv2-grid ppv2-grid-cols-1 md:ppv2-grid-cols-2 ppv2-mb-4"
                            :style="{ gap: normalizeSize(16, true) }"
                        >
                            <!-- Name -->
                            <CustomInput
                                v-model="form.name"
                                :label="$t('tag.nameLabel')"
                                :placeholder="$t('tag.namePlaceholder')"
                                :required="true"
                                customLabelClass="poppinsLight ppv2-text-black"
                                :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                :errorText="errors.name ? errors.name[0] : ''"
                                :state="errors.name ? 'error' : 'default'"
                            />
                            <!-- Parent Tag -->
                            <BaseSelect
                                v-model="form.parent_id"
                                :label="$t('tag.parentLabel')"
                                :placeholder="$t('tag.parentPlaceholder')"
                                :options="tagCategories"
                                optionLabel="name"
                                optionValue="id"
                                customLabelClass="poppinsLight ppv2-text-black"
                                :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                :errorText="errors.parent_id ? errors.parent_id[0] : ''"
                                :state="errors.parent_id ? 'error' : 'default'"
                                :clearable="true"
                            />
                        </div>

                        <!-- Description -->
                        <BaseTextarea
                            v-model="form.description"
                            :label="$t('tag.descriptionLabel')"
                            :placeholder="$t('tag.descriptionPlaceholder')"
                            :useEditor="true"
                            customLabelClass="poppinsLight ppv2-text-black"
                            :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                        />

                        <!-- Illustration -->
                        <div class="ppv2-mt-4">
                            <CustomInputUpload
                                :label="$t('tag.chooseImage')"
                                :required="true"
                                v-model="form.image"
                                @input="onImageUpload"
                                :errorText="errors.image ? errors.image[0] : ''"
                                :state="errors.image ? 'error' : 'default'"
                                customClassLabel="poppinsLight ppv2-text-black"
                                :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                            />
                        </div>
                    </div>
                </div>

                <!-- RIGHT COLUMN -->
                <div class="ppv2-w-full md:ppv2-w-[35%]">
                    <!-- Visibility Card -->
                    <div
                        class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-p-6 ppv2-mb-6 ppv2-shadow-none"
                    >
                        <BaseSwitch
                            v-model="form.is_active"
                            :label="$t('tag.activeLabel')"
                            :boxed="true"
                            :variant="form.is_active ? 'success' : 'gray'"
                            customLabelClass="poppinsLight ppv2-text-black"
                            :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                        />
                        <div class="ppv2-mt-4">
                            <BaseSwitch
                                v-model="form.is_visible"
                                :label="$t('tag.visibilityLabel')"
                                :boxed="true"
                                :variant="form.is_visible ? 'success' : 'gray'"
                                customLabelClass="poppinsLight ppv2-text-black"
                                :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                            />
                        </div>
                        <a
                            v-if="isEdit && form.is_visible && front_base_url"
                            :href="`${front_base_url}/discover/tag/${form.slug}?id=${form.id}`"
                            target="_blank"
                            class="ppv2-flex ppv2-items-center ppv2-space-x-2 ppv2-w-full ppv2-border ppv2-px-3 ppv2-py-2 ppv2-border-[#E5E7EB] justify-content-center gap-2 ppv2-mt-6"
                            :style="{ fontSize: normalizeFontSize(14), boxShadow: '0px 2px 4px 0px #00000040' }"
                        >
                            <span class="ppv2-text-black">
                                {{ $t('tag.viewOnSite') }}
                                <ExternalLink :size="16" />
                            </span>
                        </a>
                    </div>
                </div>
            </div>
            <div class="ppv2-flex ppv2-flex-col md:ppv2-flex-row" :style="{ gap: '2%' }">
                <!-- Bottom Actions -->
                <div class="ppv2-w-full md:ppv2-w-[63%]">
                    <!-- Footer Actions -->
                    <div class="ppv2-flex ppv2-justify-between ppv2-items-center ppv2-mt-8">
                        <BackButton type="button" @click="$emit('cancel')" />
                        <BaseButton
                            type="submit"
                            variant="primary"
                            :loading="isLoading"
                            :customHeight="48"
                            customClass="!ppv2-min-w-[160px] !ppv2-bg-[#7D2DBD] hover:!ppv2-bg-[#6b26a3]"
                        >
                            {{ isLoading ? $t('common.saving') : $t('common.confirm') }}
                        </BaseButton>
                    </div>
                </div>
            </div>
        </form>

        <!-- Loading State -->
        <div v-else class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-py-20">
            <div
                class="ppv2-animate-spin ppv2-rounded-full ppv2-h-12 ppv2-w-12 ppv2-border-4 ppv2-border-[#7D2DBD] ppv2-border-t-transparent"
            ></div>
        </div>
    </v-app>
</template>

<script>
import CustomInput from '../../Inputs/CustomInput.vue'
import CustomInputUpload from '../../Inputs/CustomInputUpload.vue'
import BaseSelect from '../../Shared/BaseSelect.vue'
import BaseTextarea from '../../Shared/BaseTextarea.vue'
import BaseSwitch from '../../Shared/BaseSwitch.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import BackButton from '../../Shared/BackButton.vue'
import normalizeSize from '../../../utils/normalizeSize'
import { ExternalLink } from 'lucide-vue'
import normalizeFontSize from '../../../utils/normalizeFontSize'

export default {
    name: 'PrestationTagForm',
    components: {
        CustomInput,
        CustomInputUpload,
        BaseSelect,
        BaseTextarea,
        BaseSwitch,
        BaseButton,
        BackButton,
        ExternalLink,
    },
    props: {
        editItem: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            isLoading: false,
            isDataLoading: false,
            form: {
                name: '',
                parent_id: null,
                description: '',
                general_description: '',
                image: null,
                is_visible: true,
                is_active: true,
                slug: '',
                id: null,
            },
            front_base_url: '',
            tagCategories: [],
            errors: {},
        }
    },
    computed: {
        isEdit() {
            return !!this.editItem
        },
    },
    async created() {
        this.isDataLoading = true
        try {
            const promises = [this.fetchInitialData()]
            if (this.editItem) {
                promises.push(this.fetchTagData(this.editItem.id))
            }
            await Promise.all(promises)
        } finally {
            this.isDataLoading = false
        }
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        async fetchTagData(id) {
            try {
                const response = await axios.get(`/back-office/v3/tag/show/${id}`)
                if (response.data.success) {
                    const data = response.data.data
                    this.form.name = data.name || ''
                    this.form.parent_id = data.parent_id || null
                    this.form.description = data.description || ''
                    this.form.general_description = data.general_description || ''
                    this.form.image = data.image || null
                    this.form.is_visible = !!data.is_visible
                    this.form.is_active = data.hasOwnProperty('is_active') ? !!data.is_active : true
                    this.form.slug = data.slug || ''
                    this.form.id = data.id || null
                }
                if (response.data.front_base_url) {
                    this.front_base_url = response.data.front_base_url
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
            }
        },
        async fetchInitialData() {
            try {
                const params = {}
                if (this.editItem && this.editItem.id) {
                    params.exclude_tag_id = this.editItem.id
                }
                const response = await axios.get('/back-office/v3/tag/initial-data', { params })
                if (response.data.success) {
                    const parentTags = response.data.data.parentTags || []
                    this.tagCategories = [{ id: null, name: this.$t('tag.selectItemNone') || 'Aucun' }, ...parentTags]
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error fetching initial data:', error)
            }
        },
        validateForm() {
            const errors = {}
            if (!this.form.name || this.form.name.trim() === '') {
                errors.name = [this.$t('tag.nameRequired')]
            }
            return errors
        },
        async submit() {
            this.errors = this.validateForm()

            if (Object.keys(this.errors).length > 0) {
                const firstError = Object.values(this.errors)[0][0]
                this.$toast?.error(firstError)
                return
            }

            this.isLoading = true
            this.errors = {}

            try {
                const formData = new FormData()
                formData.append('name', this.form.name)
                if (this.form.parent_id) {
                    formData.append('parent_id', this.form.parent_id)
                } else {
                    formData.append('parent_id', '')
                }
                formData.append('description', this.form.description)
                formData.append('general_description', this.form.general_description)
                formData.append('is_visible', this.form.is_visible ? 1 : 0)
                formData.append('is_active', this.form.is_active ? 1 : 0)

                if (this.form.image instanceof File) {
                    formData.append('image', this.form.image)
                }

                let endpoint = ''
                if (this.isEdit) {
                    endpoint = `/back-office/v3/tag/update/${this.editItem.id}`
                } else {
                    endpoint = '/back-office/v3/tag/store'
                }

                const response = await axios.post(endpoint, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                })

                if (response.data.success) {
                    this.$toast?.success(this.$t('tag.successMessage'))
                    this.$emit('success')
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Submit error:', error)
                if (error.response) {
                    if (error.response.status === 422) {
                        this.errors = error.response.data.errors
                        // Afficher le premier message d'erreur de validation
                        const firstErrorKey = Object.keys(this.errors)[0]
                        if (firstErrorKey && this.errors[firstErrorKey][0]) {
                            this.$toast?.error(this.errors[firstErrorKey][0])
                        } else {
                            this.$toast?.error(this.$t('tag.errorMessage'))
                        }
                    } else if (error.response.data && error.response.data.message) {
                        this.$toast?.error(error.response.data.message)
                    } else {
                        this.$toast?.error(this.$t('tag.errorMessage'))
                    }
                } else {
                    this.$toast?.error(this.$t('tag.errorMessage'))
                }
            } finally {
                this.isLoading = false
            }
        },
        onImageUpload(file) {
            this.form.image = file
        },
    },
}
</script>

<style scoped>
.uppercaseFirstLetter::first-letter {
    text-transform: uppercase;
}

.actions {
    position: absolute;
    top: 6px;
    right: 8px;
    display: flex;
    gap: 10px;
    margin-top: 5px;
    opacity: 0;
    transition: opacity 0.2s ease;
}

.item-wrapper:hover .actions {
    opacity: 1;
}

.add-item {
    border: 0.67px solid #9c9af8;
    border-radius: 12px;
    padding: 30px 10px;
    text-align: center;
    color: #9c9af8;
    cursor: pointer;
    transition: 0.3s ease;
    opacity: 0.75;
    width: 85px;
    height: 85px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.add-item:hover {
    opacity: 1;
    background-color: rgba(156, 154, 248, 0.1);
}
</style>
